import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import { useOpenSandbox } from "@/contexts/ModalContext";

const navLinks = [
  { href: "#problem", label: "The Problem" },
  { href: "#philosophy", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "#calculator", label: "Calculator" },
  { href: "#neutrality", label: "Trust" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  
  const { setOpen: setSandboxOpen } = useOpenSandbox();

  const handleNavClick = () => {
    setOpen(false);
  };

  return (
    <>
    <motion.nav
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/80 backdrop-blur-xl"
    >
      <div className="container mx-auto flex h-16 items-center justify-between px-6">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <div className="relative h-8 w-8">
            <svg viewBox="0 0 100 100" className="h-full w-full">
              <defs>
                <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="hsl(190 100% 50%)" />
                  <stop offset="100%" stopColor="hsl(210 70% 50%)" />
                </linearGradient>
              </defs>
              <circle cx="50" cy="50" r="46" stroke="url(#logoGrad)" strokeWidth="2" strokeDasharray="10 5" fill="none">
                <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="20s" repeatCount="indefinite"/>
              </circle>
              <g transform="translate(10, 30) scale(0.36)">
                <path 
                  d="M40 55 C40 25 90 25 110 55 C130 85 180 85 180 55 C180 25 130 25 110 55 C90 85 40 85 40 55 Z" 
                  fill="none" 
                  stroke="url(#logoGrad)" 
                  strokeWidth="18" 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                />
              </g>
            </svg>
          </div>
          <span className="text-xl font-bold text-foreground">ClearSlot</span>
        </Link>

        {/* Desktop Navigation - Centered */}
        <div className="hidden items-center gap-8 lg:flex">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden items-center gap-4 md:flex">
          <ThemeToggle />
          <a href="/auth" className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">
            Partner Login
          </a>
          <Button
            variant="hero"
            size="default"
            onClick={() => setSandboxOpen(true)}
          >
            Try the Sandbox
          </Button>
        </div>

        {/* Mobile Menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <div className="flex items-center gap-1 lg:hidden">
            <ThemeToggle />
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-11 w-11 md:h-9 md:w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
          </div>
          <SheetContent side="right" className="w-[280px] bg-background/95 backdrop-blur-xl">
            <div className="flex flex-col gap-6 pt-8">
              <nav className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={handleNavClick}
                    className="text-lg font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
              <div className="border-t border-border pt-6 flex flex-col gap-3">
                <a
                  href="/auth"
                  onClick={handleNavClick}
                  className="text-lg font-medium text-muted-foreground transition-colors hover:text-foreground"
                >
                  Partner Login
                </a>
                <Button
                  variant="hero"
                  size="default"
                  className="w-full min-h-[44px]"
                  onClick={() => {
                    handleNavClick();
                    setSandboxOpen(true);
                  }}
                >
                  Try the Sandbox
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </motion.nav>
    </>
  );
};

export default Navbar;
